import { validarCodigoAccesoCompetencia } from "@helpers/httpRequests/httpRequestsGruposLectura/httpRequestsGruposLectura";
import { useState, useRef, useEffect } from "react";
import { Image } from "react-bootstrap";

// ─── Brand tokens (mismo sistema que LecturaEvaluacion) ───────────────────────
const C = {
  navy:      "#00152a",
  navyMid:   "#00243d",
  navyLight: "#003156",
  dark:      "#212529",
  orange:    "#ff9900",
  yellow:    "#ffcc00",
  white:     "#f8f9fa",
  muted:     "#8a9bb0",
  mutedLt:   "#b8c9d8",
};

// ─── CSS (mismas clases base + extensiones para esta pantalla) ────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Nunito+Sans:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  /* ── Root ── */
  .ev {
    height: 100vh;
    overflow-y: auto;
    background: ${C.navy};
    font-family: 'Nunito Sans', sans-serif;
    color: ${C.white};
    position: relative;
  }

  /* ── Animated mesh background ── */
  .ev-bg {
    position: fixed; inset: 0; z-index: 0; pointer-events: none; overflow: hidden;
  }
  .ev-bg-orb {
    position: absolute; border-radius: 50%; filter: blur(100px); opacity: .18;
  }
  .ev-bg-orb1 {
    width: 600px; height: 600px;
    background: radial-gradient(circle, ${C.orange}, transparent 70%);
    top: -160px; left: -160px;
    animation: ev-drift1 14s ease-in-out infinite alternate;
  }
  .ev-bg-orb2 {
    width: 500px; height: 500px;
    background: radial-gradient(circle, ${C.yellow}, transparent 70%);
    bottom: -140px; right: -140px;
    animation: ev-drift2 18s ease-in-out infinite alternate;
  }
  .ev-bg-orb3 {
    width: 300px; height: 300px;
    background: radial-gradient(circle, ${C.navyLight}, transparent 70%);
    top: 40%; left: 50%;
    animation: ev-drift3 22s ease-in-out infinite alternate;
  }
  .ev-grid-lines {
    position: absolute; inset: 0;
    background-image:
      linear-gradient(${C.navyLight}22 1px, transparent 1px),
      linear-gradient(90deg, ${C.navyLight}22 1px, transparent 1px);
    background-size: 48px 48px;
  }

  /* ── Topbar ── */
  .ev-bar {
    position: sticky; top: 0; z-index: 100;
    background: ${C.navy}ef; backdrop-filter: blur(14px);
    border-bottom: 1px solid ${C.navyLight};
    padding: 12px 28px;
    display: flex; align-items: center; gap: 10px;
  }
  .ev-bar-dot {
    width: 10px; height: 10px; border-radius: 50%;
    background: linear-gradient(135deg, ${C.orange}, ${C.yellow});
    flex-shrink: 0;
  }
  .ev-bar-name {
    font-family: 'Nunito', sans-serif;
    font-size: 14px; font-weight: 800; color: ${C.white};
  }
  .ev-bar-sep { color: ${C.navyLight}; margin: 0 4px; }
  .ev-bar-sub {
    font-family: 'Nunito', sans-serif;
    font-size: 13px; font-weight: 600; color: ${C.muted};
  }

  /* ── Main layout ── */
  .ev-main {
    position: relative; z-index: 1;
    min-height: calc(100vh - 53px);
    display: flex; align-items: center; justify-content: center;
    padding: 40px 24px 60px;
  }
  .ev-inner {
    width: 100%; max-width: 1060px;
    display: grid; grid-template-columns: 1fr 1fr; gap: 40px; align-items: center;
  }

  /* ── Left column ── */
  .ev-left {
    display: flex; flex-direction: column; gap: 20px;
  }
  .ev-chip-row {
    display: flex; gap: 8px; flex-wrap: wrap;
  }
  .ev-chip {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 13px; border-radius: 100px;
    font-family: 'Nunito', sans-serif;
    font-size: 11px; font-weight: 700;
    letter-spacing: .08em; text-transform: uppercase;
  }
  .ev-chip-orange { background: ${C.orange}18; border: 1.5px solid ${C.orange}55; color: ${C.orange}; }
  .ev-chip-yellow { background: ${C.yellow}18; border: 1.5px solid ${C.yellow}55; color: ${C.yellow}; }
  .ev-chip-ghost  { background: transparent; border: 1.5px solid ${C.navyLight}; color: ${C.muted}; }

  .ev-eyebrow {
    font-family: 'Nunito', sans-serif;
    font-size: 15px; font-weight: 600; color: ${C.muted};
    line-height: 1.5;
  }
  .ev-title {
    font-family: 'Nunito', sans-serif;
    font-size: clamp(32px, 4.5vw, 58px);
    font-weight: 900; line-height: 1.1; color: ${C.white};
  }
  .ev-title-accent {
    background: linear-gradient(90deg, ${C.orange}, ${C.yellow});
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .ev-accent {
    width: 52px; height: 4px; border-radius: 2px;
    background: linear-gradient(90deg, ${C.orange}, ${C.yellow});
  }
  .ev-desc {
    font-size: 15px; line-height: 1.75; color: ${C.muted}; max-width: 440px;
  }
  .ev-logos {
    display: flex; align-items: center; gap: 20px; flex-wrap: wrap; margin-top: 8px;
  }
  .ev-logo-wrap {
    display: flex; align-items: center; justify-content: center;
    background: ${C.navyMid};
    border: 1px solid ${C.navyLight};
    border-radius: 12px; padding: 12px 20px;
  }
  .ev-logo-wrap img { height: 36px; width: auto; object-fit: contain; }

  /* ── Right column: card ── */
  .ev-card {
    background: ${C.navyMid};
    border: 1px solid ${C.navyLight};
    border-radius: 20px;
    border-top: 3px solid ${C.orange};
    padding: 40px 36px;
    display: flex; flex-direction: column; gap: 28px;
    box-shadow: 0 24px 64px #00000055;
  }
  .ev-card-header { display: flex; flex-direction: column; gap: 10px; }
  .ev-card-title {
    font-family: 'Nunito', sans-serif;
    font-size: 20px; font-weight: 900; color: ${C.white}; line-height: 1.3;
  }
  .ev-card-sub {
    font-size: 14px; color: ${C.muted}; line-height: 1.6;
  }
  .ev-card-sub strong { color: ${C.orange}; font-weight: 700; }

  /* ── Verification code input ── */
  .ev-code-wrap {
    display: flex; flex-direction: column; gap: 16px;
  }
  .ev-code-label {
    font-family: 'Nunito', sans-serif;
    font-size: 11px; font-weight: 700; letter-spacing: .1em;
    text-transform: uppercase; color: ${C.muted};
  }
  .ev-code-inputs {
    display: flex; gap: 12px; justify-content: center;
  }
  .ev-code-cell {
    width: 64px; height: 72px; border-radius: 14px;
    border: 2px solid ${C.navyLight};
    background: ${C.navy};
    color: ${C.white};
    font-family: 'Nunito', sans-serif;
    font-size: 28px; font-weight: 900;
    text-align: center;
    outline: none;
    caret-color: ${C.orange};
    transition: border-color .2s, box-shadow .2s, background .2s;
    letter-spacing: .04em;
    -webkit-appearance: none;
  }
  .ev-code-cell:focus {
    border-color: ${C.orange};
    background: ${C.orange}0a;
    box-shadow: 0 0 0 4px ${C.orange}22;
  }
  .ev-code-cell.filled {
    border-color: ${C.orange}88;
    color: ${C.orange};
  }
  .ev-code-cell.error {
    border-color: #ef4444;
    background: #ef444408;
    animation: ev-shake .35s ease;
  }

  /* ── Status messages ── */
  .ev-msg {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 16px; border-radius: 10px;
    font-family: 'Nunito', sans-serif; font-size: 13px; font-weight: 700;
  }
  .ev-msg-error   { background: #1a0505; border: 1px solid #7f1d1d; color: #f87171; }
  .ev-msg-success { background: #052013; border: 1px solid #1a5c36; color: #4ade80; }
  .ev-msg-loading {
    background: ${C.navyMid}; border: 1px solid ${C.navyLight}; color: ${C.muted};
  }
  .ev-spinner {
    width: 16px; height: 16px; border-radius: 50%; flex-shrink: 0;
    border: 2px solid ${C.navyLight}; border-top-color: ${C.orange};
    animation: ev-spin .8s linear infinite;
  }

  /* ── Divider ── */
  .ev-divider {
    display: flex; align-items: center; gap: 12px; color: ${C.navyLight};
  }
  .ev-divider::before, .ev-divider::after {
    content: ''; flex: 1; height: 1px; background: ${C.navyLight};
  }
  .ev-divider span {
    font-size: 11px; font-weight: 700; letter-spacing: .08em;
    text-transform: uppercase; color: ${C.muted}; white-space: nowrap;
  }

  /* ── Footer hint ── */
  .ev-hint {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 14px 16px; border-radius: 10px;
    background: ${C.orange}08; border: 1px solid ${C.orange}22;
    font-size: 13px; color: ${C.muted}; line-height: 1.5;
  }
  .ev-hint-icon { font-size: 16px; flex-shrink: 0; margin-top: 1px; }

  /* ── Step dots ── */
  .ev-steps {
    display: flex; align-items: center; gap: 8px; justify-content: center;
  }
  .ev-step { width: 8px; height: 8px; border-radius: 50%; transition: all .3s; }
  .ev-step.active {
    width: 24px; border-radius: 4px;
    background: linear-gradient(90deg, ${C.orange}, ${C.yellow});
  }
  .ev-step.done  { background: ${C.orange}66; }
  .ev-step.idle  { background: ${C.navyLight}; }

  /* ── Responsive ── */
  @media (max-width: 768px) {
    .ev-inner { grid-template-columns: 1fr; gap: 28px; }
    .ev-left  { display: none; }
    .ev-card  { padding: 28px 22px; }
    .ev-code-cell { width: 56px; height: 64px; font-size: 24px; }
    .ev-mobile-header { display: flex !important; }
  }
  .ev-mobile-header {
    display: none; flex-direction: column; align-items: center;
    gap: 12px; margin-bottom: 4px; text-align: center;
  }
  .ev-mobile-title {
    font-family: 'Nunito', sans-serif;
    font-size: 18px; font-weight: 900; color: ${C.white}; line-height: 1.3;
  }

  /* ── Animations ── */
  @keyframes ev-drift1 { from{transform:translate(0,0) scale(1)} to{transform:translate(60px,40px) scale(1.1)} }
  @keyframes ev-drift2 { from{transform:translate(0,0) scale(1)} to{transform:translate(-50px,-30px) scale(1.08)} }
  @keyframes ev-drift3 { from{transform:translate(-50%,-50%) scale(1)} to{transform:translate(-60%,-40%) scale(1.15)} }
  @keyframes ev-spin   { to{transform:rotate(360deg)} }
  @keyframes ev-shake  { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-6px)} 75%{transform:translateX(6px)} }
  @keyframes ev-fadeup { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
  @keyframes ev-pulse  { 0%,100%{opacity:1} 50%{opacity:.4} }

  .ev-up0 { animation: ev-fadeup .5s ease both; }
  .ev-up1 { animation: ev-fadeup .5s ease .08s both; }
  .ev-up2 { animation: ev-fadeup .5s ease .16s both; }
  .ev-up3 { animation: ev-fadeup .5s ease .24s both; }
`;

// ─── VerificationCodeInput ────────────────────────────────────────────────────
function VerificationCodeInput({ length = 4, onComplete, hasError = false }) {
  const [vals, setVals] = useState(Array(length).fill(""));
  const refs = useRef([]);

  // propagate error reset when hasError changes
  useEffect(() => {
    if (!hasError) return;
    // on new error: clear & refocus first
    setVals(Array(length).fill(""));
    setTimeout(() => refs.current[0]?.focus(), 50);
  }, [hasError, length]);

  const handleChange = (i, e) => {
    const ch = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...vals];
    next[i] = ch;
    setVals(next);
    if (ch && i < length - 1) refs.current[i + 1]?.focus();
    if (next.every((v) => v !== "")) onComplete?.(next.join(""));
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !vals[i] && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowLeft"  && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < length - 1) refs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(length).fill("");
    pasted.split("").forEach((ch, idx) => { next[idx] = ch; });
    setVals(next);
    const focusIdx = Math.min(pasted.length, length - 1);
    refs.current[focusIdx]?.focus();
    if (next.every((v) => v !== "")) onComplete?.(next.join(""));
  };

  return (
    <div className="ev-code-inputs" onPaste={handlePaste}>
      {vals.map((v, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          className={`ev-code-cell${v ? " filled" : ""}${hasError ? " error" : ""}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={v}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          autoComplete="off"
        />
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EventoAcceso({setShowEvaluacion, onAccessGranted, validarCodigoAcceso, usuarioLogeado }) {
  const [status, setStatus] = useState("idle"); // idle | loading | error | success
  const [errorMsg, setErrorMsg] = useState("");

  const handleComplete = async (code) => {
    setStatus("loading");
    setErrorMsg("");
    try {
      // Use prop validator if provided, else mock
      const fn = validarCodigoAcceso ?? mockValidator;
      await fn(code);
      setStatus("success");
      setTimeout(() => onAccessGranted?.(code), 900);
      setShowEvaluacion(true);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.message ?? "Código incorrecto. Verifica e intenta de nuevo.");
    }
  };

  // Mock validator — remove when wiring real logic
  const mockValidator = async (code) => {
    try{
      await validarCodigoAccesoCompetencia({code, idPerfil: usuarioLogeado.idPerfil});
    }catch(error){
      console.log("Codigo de acceso invalido");
      throw new Error("Código incorrecto. Verifica e intenta de nuevo.");
    }
    // if (code !== "1234") 
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="ev">

        {/* ── Animated background ── */}
        <div className="ev-bg" aria-hidden="true">
          <div className="ev-grid-lines" />
          <div className="ev-bg-orb ev-bg-orb1" />
          <div className="ev-bg-orb ev-bg-orb2" />
          <div className="ev-bg-orb ev-bg-orb3" />
        </div>

        {/* ── Topbar ── */}
        <div className="ev-bar">
          <span className="ev-bar-dot" />
          <span className="ev-bar-name">Lectura Inteligente</span>
          <span className="ev-bar-sep">·</span>
          <span className="ev-bar-sub">Evento de competencias</span>
        </div>

        {/* ── Main ── */}
        <div className="ev-main">
          <div className="ev-inner">

            {/* ── Left: Branding ── */}
            <div className="ev-left ev-up0">
              <div className="ev-chip-row">
                <span className="ev-chip ev-chip-orange">🏆 Evento de competencias</span>
                {/* <span className="ev-chip ev-chip-yellow">✨ </span> */}
              </div>

              <div>
                {/* <p className="ev-eyebrow">Segundo evento de calificación</p> */}
                <h1 className="ev-title">
                  Comprensión y{" "}
                  <span className="ev-title-accent">Lectura Inteligente</span>
                </h1>
              </div>

              <div className="ev-accent" />

              {usuarioLogeado.rol !== 'lectura_veloz' ? (
                <p className="ev-desc">
                  Demuestra tus habilidades de comprensión lectora en este evento especial.
                </p>
              ) : (
                <p className="ev-desc">
                  Demuestra tus habilidades de comprensión lectora en este evento especial.
                  Tu desempeño formará parte de tu evaluación de cierre del curso y tendrás la oportunidad de de ganar un setup completo para tus estudios.
                </p>

              )}


              {/* Stats row */}
              <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginTop:4 }}>
                {[
                  { icon:"📖", val:"de lectura", lbl:"Desafio" },
                  { icon:"⏱️", val:"de tiempo",  lbl:"Sin límite" },
                  { icon:"🎯", val:"tu velocidad y comprensión lectora",    lbl:"Demuestra" },
                ].map((s) => (
                  <div key={s.lbl} style={{
                    background: C.navyMid, border:`1px solid ${C.navyLight}`,
                    borderRadius:12, padding:"12px 16px",
                    display:"flex", flexDirection:"column", gap:2,
                  }}>
                    <span style={{ fontSize:11, color:C.muted, fontFamily:"'Nunito',sans-serif", fontWeight:700, letterSpacing:".08em", textTransform:"uppercase" }}>
                      {s.icon} {s.lbl}
                    </span>
                    <span style={{ fontFamily:"'Nunito',sans-serif", fontSize:16, fontWeight:900, color:C.white }}>
                      {s.val}
                    </span>
                  </div>
                ))}
              </div>

              {/* Logos */}
              <div className="ev-logos" style={{ marginTop:8 }}>
                {/* Replace src with your actual imports */}
                {/* <div className="ev-logo-wrap">
                  <span style={{ fontFamily:"'Nunito',sans-serif", fontSize:13, fontWeight:800, color:C.mutedLt }}>
                    <Image src={require('@assets/images/logo-gc-horizontal.png')} width={100} />
                  </span>
                </div>
                <div className="ev-logo-wrap">
                  <span style={{ fontFamily:"'Nunito',sans-serif", fontSize:13, fontWeight:800, color:C.mutedLt }}>
                    <Image src={require('@assets/images/LogoUvegBlanco.png')} width={100} />
                  </span>
                </div> */}
                {/* Uncomment when you have the image imports: */}
                <div className="ev-logo-wrap">
                  <img src={require('@assets/images/logo-gc-horizontal.png')} alt="Logo GC" />
                </div>

                {usuarioLogeado === "lectura_veloz" && (
                  <div className="ev-logo-wrap">
                    <img src={require('@assets/images/uvegActualizado.png')} alt="Logo UVEG" />
                  </div>
                )}
               
              </div>
            </div>

            {/* ── Right: Form card ── */}
            <div className="ev-card ev-up1">

              {/* Mobile-only header */}
              <div className="ev-mobile-header">
                <div className="flex flex-row items-center gap-5 mb-4">
                  <Image src={require('@assets/images/logo-gc-horizontal.png')} width={100} />
                  
                  {usuarioLogeado === "lectura_veloz" && (
                    <Image src={require('@assets/images/uvegActualizado.png')} width={100} />
                  )}
                </div>

                <div className="ev-chip-row" style={{ justifyContent:"center" }}>
                  <span className="ev-chip ev-chip-orange">🏆 Evento de competencias</span>
                </div>
                <p className="ev-mobile-title">
                  Comprensión y Lectura Inteligente
                </p>
              </div>

              {/* Card header */}
              <div className="ev-card-header">
                <p style={{ fontFamily:"'Nunito',sans-serif", fontSize:11, fontWeight:700, letterSpacing:".12em", textTransform:"uppercase", color:C.orange }}>
                  Acceso al evento
                </p>

                <h2 className="ev-card-title">
                  {usuarioLogeado.rol === 'lectura_veloz' ? 'Bienvenido al tercer evento de calificación' : 'Bienvenido al evento de competencias Global Click' }
                </h2>

                {usuarioLogeado.rol !== "lectura_veloz" ? (
                  <p className="ev-card-sub">
                    Ingresa el <strong>código de 4 dígitos</strong> enviado a tu correo para comenzar.
                  </p>
                ) : (
                  <p className="ev-card-sub">
                    Ingresa el <strong>código de 4 dígitos</strong> enviado a tu correo institucional para comenzar.
                  </p>

                )}

              </div>

              {/* Step dots */}
              {/* <div className="ev-steps">
                {["Acceso","Lectura","Evaluación","Resultados"].map((s, i) => (
                  <div key={s} style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div className={`ev-step ${i===0?"active":"idle"}`} title={s} />
                    {i < 3 && <div style={{ width:20, height:1, background:C.navyLight }} />}
                  </div>
                ))}
              </div> */}

              {/* Code input */}
              <div className="ev-code-wrap">
                <p className="ev-code-label">Código de acceso</p>
                <VerificationCodeInput
                  length={4}
                  onComplete={handleComplete}
                  hasError={status === "error"}
                />
              </div>

              {/* Status message */}
              {status === "loading" && (
                <div className="ev-msg ev-msg-loading">
                  <span className="ev-spinner" />
                  Verificando código de acceso...
                </div>
              )}
              {status === "error" && (
                <div className="ev-msg ev-msg-error">
                  <span>⚠️</span>
                  {errorMsg}
                </div>
              )}
              {status === "success" && (
                <div className="ev-msg ev-msg-success">
                  <span>✅</span>
                  ¡Código válido! Iniciando el evento...
                </div>
              )}

              <div className="ev-divider"><span>información</span></div>

              {/* Hint */}
              <div className="ev-hint">
                <span className="ev-hint-icon">💡</span>
                <span>
                  {usuarioLogeado.rol === 'lectura_veloz' ? 'Revisa tu bandeja de entrada o spam en tu correo institucional. Si no lo recibiste, contacta a tu instructor.' : 'Una vez que realices esta prueba no podrás ingresar nuevamente.'}
                  
                </span>
              </div>

            </div>
          </div>
        </div>

      </div>
    </>
  );
}